// One header row per commodity-group level. A parent cell spans every column
// under it, and the next row splits into those children. A group that is itself
// a column keeps its own cell and covers the header rows beneath it.

type HeaderNode = {
  id: string;
  name: string;
  number: string;
  depth: number;
};

export type CommodityGroupHeadCell = {
  key: string;
  name: string;
  number: string;
  colSpan: number;
  rowSpan: number;
  own: boolean;
  depth: number;
  rootId: string;
};

function pathsFromDepth(columns: HeaderNode[]): HeaderNode[][] {
  const paths: HeaderNode[][] = [];
  const stack: HeaderNode[] = [];
  columns.forEach((column) => {
    while (stack.length > 0 && stack[stack.length - 1].depth >= column.depth) {
      stack.pop();
    }
    stack.push(column);
    paths.push(stack.slice());
  });
  return paths;
}

function buildTreeHeader(columns: HeaderNode[], paths: HeaderNode[][]): CommodityGroupHeadCell[][] {
  const maxDepth = paths.reduce((max, path) => Math.max(max, path.length), 1);
  const covered: boolean[][] = [];
  for (let depth = 0; depth < maxDepth; depth += 1) {
    covered.push(columns.map(() => false));
  }
  const rows: CommodityGroupHeadCell[][] = [];
  for (let depth = 0; depth < maxDepth; depth += 1) {
    rows.push([]);
  }
  for (let depth = 0; depth < maxDepth; depth += 1) {
    let index = 0;
    while (index < columns.length) {
      if (covered[depth][index] || depth >= paths[index].length) {
        index += 1;
        continue;
      }
      const path = paths[index];
      const node = path[depth];
      const own = depth === path.length - 1;
      const rowSpan = own ? maxDepth - depth : 1;
      let colSpan = 1;
      if (!own) {
        while (index + colSpan < columns.length && !covered[depth][index + colSpan]) {
          const next = paths[index + colSpan];
          if (next.length <= depth || next[depth].id !== node.id) {
            break;
          }
          colSpan += 1;
        }
      }
      rows[depth].push({
        key: `${node.id}-${depth}-${index}`,
        name: node.name,
        number: node.number,
        colSpan,
        rowSpan,
        own,
        depth,
        rootId: path[0].id,
      });
      for (let row = depth; row < depth + rowSpan; row += 1) {
        for (let col = index; col < index + colSpan; col += 1) {
          covered[row][col] = true;
        }
      }
      index += colSpan;
    }
  }
  return rows;
}

export function commodityGroupHeaderRows(columns: Array<{ uuid: string; name: string; number: any; depth: number }>): CommodityGroupHeadCell[][] {
  const nodes = columns.map((column) => ({
    id: column.uuid,
    name: column.name,
    number: column.number == null ? '' : String(column.number),
    depth: column.depth,
  }));
  return buildTreeHeader(nodes, pathsFromDepth(nodes));
}
